/**
 * useVehicleStatuses - Catálogo híbrido de Status do veículo
 *
 * Status de SISTEMA (Ativo/Cancelado, imutáveis) + status PERSONALIZADOS do tenant.
 * Mesma estrutura de useLeadSources: leitura e escrita somente via RPC SECURITY DEFINER.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-02 v1.0.0 - Criação inicial.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('VehicleStatuses');

/** Tipo base: define como o status conta em filtros e números */
export type VehicleBaseStatus = 'active' | 'cancelled';

export interface VehicleStatus {
  id: string;
  label: string;
  color: string | null;
  base_status: VehicleBaseStatus;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
}

/** Lista status (sistema + tenant). includeInactive para a tela de gestão. */
export function useVehicleStatuses(includeInactive = false) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['vehicle-statuses', tenantId, includeInactive],
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_tenant_vehicle_statuses', {
        p_include_inactive: includeInactive,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao buscar status de veículo', error);
        throw error;
      }
      return (data ?? []) as VehicleStatus[];
    },
  });

  const statuses = query.data ?? [];
  const byId = useMemo(() => new Map(statuses.map((s) => [s.id, s])), [statuses]);

  return {
    statuses,
    activeStatuses: useMemo(() => statuses.filter((s) => s.is_active), [statuses]),
    byId,
    isLoading: query.isLoading,
  };
}

export interface UpsertVehicleStatusInput {
  id?: string | null;
  label: string;
  color?: string | null;
  base_status: VehicleBaseStatus;
  sort_order?: number;
  is_active?: boolean;
}

/** Invalida tudo que exibe status */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['vehicle-statuses'] });
    qc.invalidateQueries({ queryKey: ['tenant-vehicles'] });
  };
}

/** Cria/edita status personalizado */
export function useUpsertVehicleStatus() {
  const { currentTenant } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: UpsertVehicleStatusInput) => {
      const { data, error } = await (supabase.rpc as any)('upsert_tenant_vehicle_status', {
        p_id: input.id ?? null,
        p_label: input.label,
        p_color: input.color ?? null,
        p_base_status: input.base_status,
        p_sort_order: input.sort_order ?? 0,
        p_is_active: input.is_active ?? true,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => { invalidate(); toast.success('Status salvo'); },
    onError: (e: Error) => { logger.error('Falha ao salvar status', e); toast.error(e.message || 'Erro ao salvar status'); },
  });
}

/** Exclui (ou desativa, se em uso) status personalizado */
export function useDeleteVehicleStatus() {
  const { currentTenant } = useAuth();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase.rpc as any)('delete_tenant_vehicle_status', {
        p_id: id,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return data as { mode: 'hard_deleted' | 'soft_deleted'; vehicles_using: number };
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(r.mode === 'soft_deleted'
        ? `Status desativado — ${r.vehicles_using} veículo(s) ainda o utilizam.`
        : 'Status excluído');
    },
    onError: (e: Error) => { logger.error('Falha ao excluir status', e); toast.error(e.message || 'Erro ao excluir status'); },
  });
}
